/**
 * Status vocabulary for the /merchant Staff area + mappers from the backend
 * enums to the mockup's pill variants/labels, and from the StatusTabs filter
 * to the backend query params.
 *
 * Shared contract (fixed by TASK-PLAN P3). The 7 screen-agents and the P5
 * hooks import these types and helpers — no signature changes without
 * updating the plan.
 */

/** Mirrors backend `DocumentStatusEnum` (user document review lifecycle). */
export type DocumentStatus = 'NOT_REQUIRED' | 'PENDING' | 'APPROVED' | 'REJECTED';

/** Mirrors backend `MerchantStatusEnum`. */
export type MerchantStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'PENDING_OWNER_ACTIVATION';

/** Mirrors backend `RoleEnum`. */
export type Role = 'CUSTOMER' | 'MERCHANT' | 'COURIER' | 'ADMIN' | 'STAFF';

/** Visual variants the `StatusPill` primitive understands (see theme tokens). */
export type PillVariant = 'active' | 'pending' | 'suspended' | 'rejected' | 'soft';

/** Tab keys of the `StatusTabs` segmented filter (`ativacao` is shop-list-only). */
export type StatusTab =
  | 'todos'
  | 'ativo'
  | 'pendente'
  | 'ativacao'
  | 'suspenso'
  | 'rejeitado'
  // Eixo de DOCUMENTO (lista de entregadores): aprovado e "sem documento". Este último é o buraco
  // do auto-cadastro — o entregador nasce NOT_REQUIRED e não aparece em nenhuma fila de PENDING.
  | 'aprovado'
  | 'sem_documento';

/** A resolved pill: which variant to paint + the PT-BR label to show. */
export interface PillSpec {
  variant: PillVariant;
  label: string;
}

/**
 * User (person) status → pill. Document review takes precedence over the
 * active flag: a PENDING/REJECTED document shows that state regardless of
 * `isActive`; once the document is settled, the active flag decides
 * Ativo vs Suspenso.
 */
export function userStatusPill(u: { documentStatus: DocumentStatus; isActive: boolean }): PillSpec {
  if (u.documentStatus === 'PENDING') return { variant: 'pending', label: 'Pendente' };
  if (u.documentStatus === 'REJECTED') return { variant: 'rejected', label: 'Rejeitado' };
  return u.isActive
    ? { variant: 'active', label: 'Ativo' }
    : { variant: 'suspended', label: 'Suspenso' };
}

/**
 * Merchant (shop) status → pill. `PENDING_OWNER_ACTIVATION` has its own label:
 * it waits on the OWNER accepting the invite, not on staff approval — collapsing
 * both into "Pendente" hid the difference (and the whole onboarding queue).
 */
export function merchantStatusPill(s: MerchantStatus): PillSpec {
  switch (s) {
    case 'APPROVED':
      return { variant: 'active', label: 'Ativo' };
    case 'PENDING':
      return { variant: 'pending', label: 'Pendente' };
    case 'PENDING_OWNER_ACTIVATION':
      return { variant: 'pending', label: 'Aguard. ativação' };
    case 'SUSPENDED':
      return { variant: 'suspended', label: 'Suspenso' };
    case 'REJECTED':
      return { variant: 'rejected', label: 'Rejeitado' };
    default:
      return { variant: 'soft', label: '—' };
  }
}

/**
 * StatusTabs key → user-list backend filter. `pendente`/`rejeitado` filter on
 * `documentStatus`; `ativo`/`suspenso` filter on `isActive`; `todos` clears
 * all filters.
 */
export function userStatusFilter(tab: StatusTab): { documentStatus?: DocumentStatus; isActive?: boolean } {
  switch (tab) {
    case 'pendente':
      return { documentStatus: 'PENDING' };
    case 'rejeitado':
      return { documentStatus: 'REJECTED' };
    case 'suspenso':
      return { isActive: false };
    case 'ativo':
      return { isActive: true };
    case 'todos':
    default:
      return {};
  }
}

/**
 * StatusTabs key → merchant-list backend filter (single `status` param).
 * `todos` clears the filter.
 */
export function merchantStatusFilter(tab: StatusTab): { status?: MerchantStatus } {
  switch (tab) {
    case 'ativo':
      return { status: 'APPROVED' };
    case 'pendente':
      return { status: 'PENDING' };
    case 'ativacao':
      return { status: 'PENDING_OWNER_ACTIVATION' };
    case 'suspenso':
      return { status: 'SUSPENDED' };
    case 'rejeitado':
      return { status: 'REJECTED' };
    case 'todos':
    default:
      return {};
  }
}

/** Default tab order rendered by `StatusTabs` (people lists — no `ativacao`). */
export const STATUS_TABS: readonly StatusTab[] = [
  'todos',
  'ativo',
  'pendente',
  'suspenso',
  'rejeitado',
];

/** Shop-list tab order: includes "Aguard. ativação" (PENDING_OWNER_ACTIVATION). */
export const SHOP_STATUS_TABS: readonly StatusTab[] = [
  'todos',
  'ativo',
  'pendente',
  'ativacao',
  'suspenso',
  'rejeitado',
];

/** Courier-list tab order: eixo DOCUMENTO, que é o que gera fila de trabalho do staff. */
export const COURIER_DOC_STATUS_TABS: readonly StatusTab[] = [
  'todos',
  'pendente',
  'aprovado',
  'rejeitado',
  'sem_documento',
];

/** `documentStatus` do backend para cada aba de entregador (`undefined` = todos). */
export const COURIER_DOC_STATUS_PARAM: Partial<Record<StatusTab, string>> = {
  pendente: 'PENDING',
  aprovado: 'APPROVED',
  rejeitado: 'REJECTED',
  sem_documento: 'NOT_REQUIRED',
};

/** PT-BR label for each tab. */
export const STATUS_TAB_LABEL: Record<StatusTab, string> = {
  todos: 'Todos',
  ativo: 'Ativo',
  pendente: 'Pendente',
  ativacao: 'Aguard. ativação',
  suspenso: 'Suspenso',
  rejeitado: 'Rejeitado',
  aprovado: 'Aprovado',
  sem_documento: 'Sem documento',
};
